/**
 * Cart Store (Zustand)
 * Global state management for shopping cart
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Cart, CartLine } from '~/lib/shopify/types';
import * as shopifyApi from '~/lib/shopify/api';
import { trackAddToCart, trackRemoveFromCart } from '~/lib/tracking/gtm';

interface CartState {
  cart: Cart | null;
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;

  initializeCart: () => Promise<void>;
  addItem: (variantId: string, quantity?: number) => Promise<void>;
  updateItem: (lineId: string, quantity: number) => Promise<void>;
  removeItem: (lineId: string) => Promise<void>;
  clearError: () => void;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      cart: null,
      isOpen: false,
      isLoading: false,
      error: null,

      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),
      toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),

      initializeCart: async () => {
        try {
          set({ isLoading: true, error: null });
          const cart = await shopifyApi.getCart();
          set({ cart, isLoading: false });
        } catch (error) {
          console.error('Failed to initialize cart:', error);
          set({ isLoading: false, error: 'Failed to load cart' });
        }
      },

      addItem: async (variantId: string, quantity = 1) => {
        try {
          set({ isLoading: true, error: null });
          const cart = await shopifyApi.addToCart(variantId, quantity);
          // Track the newly added item
          const addedLine = cart.lines.edges.find(
            (e) => e.node.merchandise.id === variantId
          );
          if (addedLine) trackAddToCart(addedLine.node);
          set({ cart, isLoading: false, isOpen: true });
        } catch (error) {
          console.error('Failed to add item:', error);
          set({ isLoading: false, error: 'Failed to add item to cart' });
        }
      },

      updateItem: async (lineId: string, quantity: number) => {
        try {
          set({ isLoading: true, error: null });

          if (quantity <= 0) {
            await get().removeItem(lineId);
            return;
          }

          const cart = await shopifyApi.updateCartLine(lineId, quantity);
          set({ cart, isLoading: false });
        } catch (error) {
          console.error('Failed to update item:', error);
          set({ isLoading: false, error: 'Failed to update cart' });
        }
      },

      removeItem: async (lineId: string) => {
        try {
          // Capture line before removal for tracking
          const currentCart = get().cart;
          const removedLine = currentCart?.lines.edges.find((e) => e.node.id === lineId);
          set({ isLoading: true, error: null });
          const cart = await shopifyApi.removeFromCart(lineId);
          if (removedLine) trackRemoveFromCart(removedLine.node);
          set({ cart, isLoading: false });
        } catch (error) {
          console.error('Failed to remove item:', error);
          set({ isLoading: false, error: 'Failed to remove item' });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'headsetsco-cart',
      partialize: (state) => ({ cart: state.cart }),
    }
  )
);

// Selectors
export const selectCartItems = (state: CartState): CartLine[] =>
  state.cart?.lines.edges.map((edge) => edge.node) || [];

export const selectCartTotal = (state: CartState): string =>
  state.cart?.cost.totalAmount.amount || '0';

export const selectCartCurrency = (state: CartState): string =>
  state.cart?.cost.totalAmount.currencyCode || 'GBP';

export const selectCartQuantity = (state: CartState): number =>
  state.cart?.totalQuantity || 0;

export const selectCheckoutUrl = (state: CartState): string | null => {
  const url = state.cart?.checkoutUrl ?? null;
  if (!url) return null;
  // Route checkout through the headless www domain so the Cloudflare worker
  // proxies the request and rewrites the checkout logo back to the headless site.
  return url
    .replace('https://itremarketing.co.uk/', 'https://www.itremarketing.co.uk/')
    .replace('https://tr9ath-e6.myshopify.com/', 'https://www.itremarketing.co.uk/');
};
